import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, MessageSquare, Plus, BarChart3, ChevronDown, ChevronRight } from 'lucide-react';
import Button from '../../components/UI/Button';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import CustomScrollbar from '../../components/UI/CustomScrollbar';
import { useHostDashboard, useRecentActivity } from '../../hooks/useDashboardData';
import { useAppSelector } from '../../store/hooks';
import { authAPI } from '../../services/api';
import { setUser } from '../../store/slices/authSlice';
import { store } from '../../store/store';
import { logError } from '../../utils/errorLogger';

const HostDashboard: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const { data: statsData, loading: statsLoading, error: statsError } = useHostDashboard();
  const { data: activityData, loading: activityLoading } = useRecentActivity();
  
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  // Refresh user profile on dashboard load to get latest roles
  // This ensures we have the most up-to-date roles (e.g., if user was added as guest after login)
  useEffect(() => {
    const refreshUserProfile = async () => {
      try {
        const profileResponse = await authAPI.getProfile();
        if (profileResponse.data.success && profileResponse.data.data) {
          const updatedUser = {
            ...profileResponse.data.data,
            roles: profileResponse.data.data.roles || [profileResponse.data.data.role],
            active_role: profileResponse.data.data.active_role || profileResponse.data.data.role,
          };
          store.dispatch(setUser(updatedUser));
        }
      } catch (err) {
        logError('Failed to refresh user profile', err);
      }
    };
    
    // Always refresh profile on dashboard load to get latest roles
    // This is important because roles can change (e.g., host added as guest) after login
    refreshUserProfile();
  }, []); // Only run once on mount



  // Show loading state
  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (statsError) {
    return (
      <div className="space-y-6">
        <div className={`rounded-lg p-6 ${
          theme === 'dark' 
            ? 'bg-red-900/30 border border-red-700/50' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>
              <Calendar className="h-6 w-6" />
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-red-300' : 'text-red-800'}`}>Error Loading Dashboard</h3>
              <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>{statsError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper to parse decimal values
  const parseDecimal = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value) || 0;
    return 0;
  };

  // Prepare stats data
  const stats = statsData ? [
    {
      name: 'Active Events',
      value: statsData.events.active.toString(),
      subtitle: `${statsData.events.total} total events`,
      icon: <Calendar />,
      trend: { value: Math.abs(statsData.events.growth_rate), positive: statsData.events.growth_rate >= 0 },
    },
    {
      name: 'Total Guests',
      value: statsData.guests.total.toString(),
      subtitle: `${statsData.guests.accepted} accepted`,
      icon: <Users />,
      trend: { value: Math.abs(statsData.guests.growth_rate), positive: statsData.guests.growth_rate >= 0 },
    },
    {
      name: 'Response Rate',
      value: `${statsData.guests.response_rate.toFixed(1)}%`,
      subtitle: `${statsData.guests.accepted + statsData.guests.rejected} of ${statsData.guests.total} responded`,
      icon: <BarChart3 />,
      trend: { value: Math.abs(statsData.guests.response_rate), positive: statsData.guests.response_rate >= 50 },
    },
    {
      name: 'Pending Responses',
      value: statsData.guests.pending.toString(),
      subtitle: `${statsData.recent_activity.new_responses_week} responses this week`,
      icon: <MessageSquare />,
      action: undefined,
    },
  ] : [];

  const upcomingEvents = statsData?.upcoming_events || [];
  const recentActivity = activityData?.activities || [];

  // Toggle event accordion expansion
  const toggleEventExpansion = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };


  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Host Dashboard</h1>
            </div>
            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Manage your events and track guest responses
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-2 sm:gap-3 flex-wrap items-center">
            <Link 
              to="/host/events/create"
              className={`inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 hover:scale-105 ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
                  : 'bg-gradient-to-r from-[#33FF00] to-[#2BD400] text-black hover:from-[#2BD400] hover:to-[#33FF00] shadow-lg hover:shadow-[#33FF00]/25'
              }`}
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-3 flex-shrink-0" />
              <span className="hidden sm:inline">Create New Event</span>
              <span className="sm:hidden">Create</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid — aligned with Admin/Guest dashboard */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto px-4 sm:px-6">
        {stats.map((item) => (
          <EnhancedLightCard
            key={item.name}
            title={item.name}
            value={item.value}
            subtitle={item.subtitle}
            icon={item.icon}
            trend={item.trend}
            action={item.action}
            compactHorizontal
          />
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 max-w-6xl mx-auto mb-8 items-start">
        {/* Upcoming Events */}
        <div className={`lg:col-span-2 relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm mb-10 ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          {/* Premium background with subtle patterns */}
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          
          {/* Subtle texture overlay */}
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          
          {/* Content */}
          <div className="relative z-10">
            <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Upcoming Events</h3>
                <Link to="/host/events">
                  <Button variant="outline" size="sm">
                    View All
                  </Button>
                </Link>
              </div>
            </div>
            <div className="px-6 py-4 pb-6">
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map((event) => {
                    const isExpanded = expandedEvents.has(event.id);
                    return (
                      <div
                        key={event.id}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          theme === 'dark' 
                            ? 'border-gray-700/50 bg-gray-800/30' 
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        {/* Accordion Header */}
                        <button
                          type="button"
                          onClick={() => toggleEventExpansion(event.id)}
                          className={`w-full flex items-center justify-between p-4 text-left hover:bg-opacity-80 transition-colors ${
                            theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50/50'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {isExpanded ? (
                              <ChevronDown className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            ) : (
                              <ChevronRight className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {event.title}
                              </h4>
                              <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                {new Date(event.date).toLocaleDateString()} at {event.time?.substring(0, 5)}
                              </p>
                              <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`}>
                                {event.location}
                              </p>
                            </div>
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {event.responses}/{event.total_guests} responses
                            </p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                              {event.response_rate.toFixed(1)}% response rate
                            </p>
                            <div className={`w-16 rounded-full h-2 mt-1 ${
                              theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200/50'
                            }`}>
                              <div
                                className="bg-gradient-to-r from-[#33FF00] to-[#2BD400] h-2 rounded-full transition-all duration-1000 ease-out shadow-sm ring-1 ring-[#33FF00]/20"
                                style={{
                                  width: `${Math.min(event.response_rate, 100)}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </button>

                        {/* Accordion Content */}
                        {isExpanded && (
                          <div className={`px-4 pb-4 pt-2 border-t ${
                            theme === 'dark' ? 'border-gray-700/50' : 'border-gray-200'
                          }`}>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Event Status
                                </span>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  event.status === 'active' 
                                    ? theme === 'dark' 
                                      ? 'bg-green-900/30 text-green-400 border border-green-700/50' 
                                      : 'bg-green-50 text-green-700 border border-green-200'
                                    : theme === 'dark'
                                      ? 'bg-gray-700/50 text-gray-300 border border-gray-600'
                                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                                }`}>
                                  {event.status}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Total Guests Invited
                                </span>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {event.total_guests}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Response Rate
                                </span>
                                <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                  {event.response_rate.toFixed(1)}%
                                </span>
                              </div>
                            </div>

                            {/* Sub-Events Section */}
                            {event.sub_events && event.sub_events.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-300/30 dark:border-gray-700/30">
                                <div className={`text-xs font-semibold mb-3 ${
                                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                  Sub-Events ({event.sub_events.length}):
                                </div>
                                <div className="space-y-3">
                                  {event.sub_events.map((subEvent) => (
                                    <div
                                      key={subEvent.id}
                                      className={`pl-4 border-l-2 rounded-r-lg py-2 ${
                                        theme === 'dark' 
                                          ? 'border-purple-500/30 bg-purple-500/5' 
                                          : 'border-purple-300 bg-purple-50/30'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <h5 className={`text-sm font-medium mb-1 ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                          }`}>
                                            {subEvent.title}
                                          </h5>
                                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {new Date(subEvent.date).toLocaleDateString()} at {subEvent.time?.substring(0, 5)}
                                          </p>
                                          <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {subEvent.location}
                                          </p>
                                        </div>
                                        <div className="text-right ml-4 flex-shrink-0">
                                          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {subEvent.responses}/{subEvent.total_guests} responses
                                          </p>
                                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                            {subEvent.response_rate.toFixed(1)}% response rate
                                          </p>
                                          <div className={`w-12 rounded-full h-1.5 mt-1 ${
                                            theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200/50'
                                          }`}>
                                            <div
                                              className="bg-gradient-to-r from-[#33FF00] to-[#2BD400] h-1.5 rounded-full transition-all duration-1000 ease-out"
                                              style={{
                                                width: `${Math.min(subEvent.response_rate, 100)}%`,
                                              }}
                                            ></div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className={`h-12 w-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}>No upcoming events</p>
                  <Link to="/host/events/create">
                    <Button className="mt-4" leftIcon={<Plus className="h-4 w-4" />}>
                      Create Your First Event
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm mb-10 ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          {/* Premium background with subtle patterns */}
          <div className={`absolute inset-0 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
              : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
          }`}></div>
          
          {/* Subtle texture overlay */}
          <div className={`absolute inset-0 opacity-5 ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
              : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
          }`}></div>
          
          
          {/* Content */}
          <div className="relative z-10">
            <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Recent Activity</h3>
            </div>
            <div className="px-6 py-4">
              {activityLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33FF00]"></div>
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="relative">
                  <CustomScrollbar maxHeight="max-h-64">
                    <div className="space-y-4">
                      {recentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-2 w-2 bg-[#33FF00] rounded-full mt-2"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{activity.message}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-black'}`}>
                              {new Date(activity.time).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CustomScrollbar>
                  {recentActivity.length > 4 && (
                    <div className={`absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t ${
                      theme === 'dark' ? 'from-gray-900' : 'from-gray-50'
                    } to-transparent pointer-events-none`}></div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className={theme === 'dark' ? 'text-white' : 'text-black'}>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default HostDashboard;
